import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';
import { useReviewEdit } from '@/module/Author/Review/model/ReviewQuery';
import { ReviewSchema, defaultReviewValues, reviewSchema } from '@/module/Author/Review/model/ReviewSchema';

import { useAppNotification } from '@feature/utils';

import { ReviewForm } from './ReviewForm';
import { Review } from './model/ReviewTypes';

interface ReviewEditProps {
  review: Review;
}

const convert = (review: Review): ReviewSchema => ({
  ...review,
  identifiers: review.identifiers ?? defaultIdentifiers,
});

export function ReviewEdit(props: ReviewEditProps) {
  const { review } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useReviewEdit(review.id);
  const notification = useAppNotification();

  const onSubmit = (values: ReviewSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(review), [review]);
  const navigate = useNavigate();

  return (
    <ReviewForm
      title={t('reviews.form.title.edit')}
      schema={reviewSchema}
      defaultValues={defaultReviewValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={review.source.type === 'SCOPUS'}
    />
  );
}
